import React from "react";
import { Link } from "react-router-dom";
import footerVideo from "../Assets/footer/footer-bg.mp4";
import logo from "../Assets/logo1.png";
import {
  FaFacebookF,
  FaInstagram,
  FaYoutube,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaChevronRight,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: t('footer_quick_links'),
      links: [
        { label: t('footer_home'), to: "/" },
        { label: t('footer_about'), to: "/about-us" },
        { label: t('footer_services'), to: "/services" },
        { label: t('footer_gallery'), to: "/gallery" },
        { label: t('footer_contact'), to: "/contact-us" },
      ],
    },
    {
      title: t('footer_legal'),
      links: [
        { label: t('footer_privacy'), to: "/privacy-policy" },
        { label: t('footer_terms'), to: "/terms-conditions" },
        { label: t('footer_cancellation'), to: "/cancellation-policy" },
        { label: t('footer_disclaimer'), to: "/disclaimer" },
      ],
    },
  ];

  return (
    <footer className="relative bg-[#0a0a0a] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-40">
        <video
          className="w-full h-full object-cover"
          src={footerVideo}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

          <div className="flex flex-col gap-6">
            <Link to="/" className="inline-block group">
              <div className="w-48 md:w-56 h-auto">
                <img src={logo} alt={t('footer_logo_alt')} className="w-full object-contain" />
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              {t('footer_desc')}
            </p>
          </div>

          {footerLinks.map((section) => (
            <div key={section.title}>
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-orange-main rounded-full" />
                {section.title}
              </h3>
              <ul className="flex flex-col gap-4">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-gray-400 hover:text-orange-main hover:translate-x-1 flex items-center gap-2 transition-all duration-300 text-sm font-medium"
                    >
                      <FaChevronRight className="text-[10px] opacity-50" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
              <span className="w-1.5 h-6 bg-orange-main rounded-full" />
              {t('footer_contact_details')}
            </h3>
            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-orange-main transition-colors duration-300 shrink-0">
                  <FaMapMarkerAlt className="text-orange-main group-hover:text-white" />
                </div>
                <div className="text-sm">

  <p className="font-bold text-gray-200">
    {t('footer_our_offices')}
  </p>

  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">

    <a
      href="https://maps.app.goo.gl/BkyjHtiCts21cGiMA"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-400 hover:text-orange-400 transition-all duration-300"
    >
      {t('footer_khajuraho')}
    </a>

    <span className="text-gray-600">•</span>

    <a
      href="https://maps.google.com/?q=F-49,+First+Floor,+Laxmibai+Parisar,+Neelbad,+Bhopal,+Madhya+Pradesh+462044"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-400 hover:text-orange-400 transition-all duration-300"
    >
      {t('footer_bhopal')}
    </a>

    <span className="text-gray-600">•</span>

    <a
      href="https://maps.google.com/?q=G20+Palika+Palace,+Panchkuian+Road,+Near+RK+Ashram+Marg+Metro+Station,+New+Delhi+110001"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-400 hover:text-orange-400 transition-all duration-300"
    >
      {t('footer_delhi')}
    </a>

    <span className="text-gray-600">•</span>

    <a
      href="https://maps.google.com/?q=Flat+No+AF-2/8,+Imlak+Colony,+Phase+2,+Nadesar,+Varanasi+221002"
      target="_blank"
      rel="noopener noreferrer"
      className="text-gray-400 hover:text-orange-400 transition-all duration-300"
    >
      {t('footer_varanasi')}
    </a>

  </div>

</div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-orange-main transition-colors duration-300 shrink-0">
                  <FaPhoneAlt className="text-orange-main group-hover:text-white" />
                </div>
                <div className="text-sm">
                  <p className="font-bold text-gray-200">{t('footer_call_us')}</p>
                  <a href="tel:+919993910094" className="text-gray-400 hover:text-white mt-1 block">+91 99939 10094</a>
                  <a href="tel:+919981980094" className="text-gray-400 hover:text-white mt-1 block">+91 99819 80094</a>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-orange-main transition-colors duration-300 shrink-0">
                  <FaEnvelope className="text-orange-main group-hover:text-white" />
                </div>
                <div className="text-sm">
                  <p className="font-bold text-gray-200">{t('footer_email_us')}</p>
                  <a href="mailto:khajurahoroutes@gmail.com" className="text-gray-400 hover:text-white mt-1 block break-all">khajurahoroutes@gmail.com</a>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              {[
                { icon: <FaFacebookF />, color: "#1877F2", link: "https://www.facebook.com/profile.php?id=100064103830605" },
                { icon: <FaInstagram />, color: "#E4405F", link: "https://www.instagram.com/routeskhajuraho/" },
                { icon: <FaYoutube />, color: "#FF0000", link: "https://www.youtube.com/@khajurahoroutes8681" },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.link}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mt-16 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500 font-medium">
          <p>{t('footer_copyright', { year: currentYear })}</p>
          <p className="flex items-center gap-1">
            {t('footer_dev_by')}
            <a href="https://yashsoftsolution.com" target="_blank" rel="noreferrer" className="text-orange-main hover:text-white transition-colors">
              Yashsoft Solution
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
